import { useQuery } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Badge } from '@/_shared/components/ui/badge';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { orpc } from '@/_shared/lib/orpc';

import { DashboardPanel } from '../components/DashboardPanel';
import { DashboardParamBar } from '../components/DashboardParamBar';
import { PublicUnavailable } from '../components/PublicUnavailable';
import { useDynamicParamOptions } from '../hooks/useDynamicParamOptions';
import { computeDefaults, searchParamsToValues, valuesToSearchParams } from '../lib/dashboard-params';
import type { DashboardPanel as DashboardPanelType } from '../types';

export function PublicDashboard(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch via share token. The backend returns { disabled: true } for
  // unknown/private/workspace-disabled cases — we collapse all of those to
  // the PublicUnavailable page so the failure mode does not leak which
  // condition fired.
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery(
    orpc.dashboard.getPublic.queryOptions({
      input: token ? { token } : { token: '' },
    }),
  );
  const dashboard = dashboardData && !dashboardData.disabled ? dashboardData.dashboard : null;
  usePageTitle(dashboard?.title);

  // Resolve dynamic parameter options. We forward the share token so the
  // anonymous backend gate (token must resolve to this dashboard) accepts the
  // request — without it the call collapses to NOT_FOUND.
  const {
    parameters: resolvedParams,
    isLoading: paramsLoading,
    loadingParamNames,
  } = useDynamicParamOptions(dashboard?.id, dashboard?.spec.parameters ?? [], orpc, token);

  // Merge defaults with URL search params
  const paramValues = useMemo((): Record<string, string | string[]> => {
    if (!dashboard) return {};
    const defaults = computeDefaults(resolvedParams);
    const fromUrl = searchParamsToValues(searchParams, resolvedParams);
    return { ...defaults, ...fromUrl };
  }, [dashboard, resolvedParams, searchParams]);

  // Deferred defaults: seed missing params AND auto-correct invalid option values once loaded
  useEffect(() => {
    if (paramsLoading || !dashboard) return;
    const patch: Record<string, string | string[]> = {};
    for (const p of resolvedParams) {
      const isDynamic = !!p.sql || p.hasDynamicOptions === true;
      if (p.type === 'select' && isDynamic && p.options?.length) {
        const currentVal = searchParams.get(p.name);
        const hasValidValue = currentVal !== null && p.options.some((o) => o.value === currentVal);
        if (!hasValidValue) {
          patch[p.name] = p.options[0]!.value;
        }
      } else if (p.type === 'multi_select' && isDynamic && p.options?.length) {
        const currentVals = searchParams.getAll(p.name);
        const validVals = currentVals.filter((v) => p.options!.some((o) => o.value === v));
        if (validVals.length === 0) {
          patch[p.name] = p.options.map((o) => o.value);
        }
      }
    }
    if (Object.keys(patch).length > 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (Array.isArray(v)) {
              for (const val of v) next.append(k, val);
            } else {
              next.set(k, v);
            }
          }
          return next;
        },
        { replace: true },
      );
    }
  }, [paramsLoading, resolvedParams, dashboard, searchParams, setSearchParams]);

  // Sync param changes to URL
  const handleParamChange = useCallback(
    (newValues: Record<string, string | string[]>) => {
      setSearchParams(valuesToSearchParams(newValues), { replace: true });
    },
    [setSearchParams],
  );

  // Sort panels by order

  const sortedPanels = useMemo(() => {
    if (!dashboard) return [];
    return [...dashboard.spec.panels].sort((a, b) => a.order - b.order);
  }, [dashboard]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  // The backend collapses unknown / private / workspace-disabled tokens into
  // a single `{ disabled: true }` shape. We collapse error and disabled cases
  // into the same PublicUnavailable page so the failure mode does not leak
  // which condition fired.
  if (error || !dashboard) {
    return <PublicUnavailable />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PublicDashboardHeader title={dashboard.title} description={dashboard.description} />
      <DashboardParamBar
        parameters={resolvedParams}
        values={paramValues}
        onChange={handleParamChange}
        loadingParamNames={loadingParamNames}
      />
      <div className="flex-1">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-6 py-6 lg:grid-cols-2">
          {sortedPanels.map((panel) => (
            <PublicPanelWithData
              key={panel.id}
              dashboardId={dashboard.id}
              shareToken={token ?? ''}
              panel={panel}
              params={paramValues}
              paramsLoading={paramsLoading}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Public header (no share controls) ───

function PublicDashboardHeader({
  title,
  description,
}: {
  title: string;
  description: string | null;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold text-foreground">{title}</h1>
        {description && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className="ml-4 flex shrink-0 items-center gap-2">
        <Badge variant="secondary" className="text-xs">
          Powered by Rudder AI · Private Beta
        </Badge>
      </div>
    </div>
  );
}

// ─── Panel with data fetching ───

function PublicPanelWithData({
  dashboardId,
  shareToken,
  panel,
  params,
  paramsLoading,
}: {
  dashboardId: string;
  shareToken: string;
  panel: DashboardPanelType;
  params: Record<string, string | string[]>;
  paramsLoading?: boolean;
}): React.ReactElement {
  const queryOpts = orpc.dashboard.query.queryOptions({
    input: {
      dashboardId,
      panelId: panel.id,
      params,
      shareToken,
    },
  });
  const { data, isLoading, error } = useQuery({
    ...queryOpts,
    enabled: !paramsLoading,
  });

  return (
    <div className={panel.width === 'full' ? 'lg:col-span-2' : 'lg:col-span-1'}>
      <DashboardPanel
        panel={panel}
        data={data ?? null}
        loading={isLoading}
        error={error ? String(error.message ?? 'Query failed') : null}
      />
    </div>
  );
}
